import { useMemo } from "react";
import axios from "axios";
import { clamp, sortBy } from "lodash-es";

import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";

import {
  Indicator,
  IndicatorBase,
  IndicatorSelection,
  Layer,
  LayerBase,
  LayerProperty,
  LayerProperties,
  LayerPropertyValue,
  Selection,
  SelectionProperty,
} from "../types";
import {
  IndicatorSelectionResponse,
  SelectionPropertyResponse,
  SelectionResponse,
} from "types/backend";

interface StatusError {
  status: number;
  statusText: string;
}

function isStatusError(error: unknown): error is StatusError {
  return (
    (error as Error).hasOwnProperty("status") &&
    (error as Error).hasOwnProperty("statusText")
  );
}

function serializeSelection(
  data: IndicatorSelection,
  i: number = 0
): IndicatorSelectionResponse {
  const { indicatorId, opacity, layerId, active } = data;
  //set all properties manually, could the object can contain more that we don't need!
  return {
    active,
    layerId,
    indicatorId,
    opacity: clamp(opacity, 0, 1),
    order: i,
  };
}

function deserializeSelection(
  data: IndicatorSelectionResponse
): IndicatorSelection {
  return {
    ...data,
    opacity: clamp(data.opacity, 0, 1),
  };
}

function deserializeLayerProperty(
  variable: SelectionPropertyResponse,
  layerProperties: LayerProperties[]
): SelectionProperty {
  const selectedValue = layerProperties.reduce(
    (
      selected: { prop: LayerProperties; value: LayerPropertyValue } | null,
      property: LayerProperties
    ) => {
      if (selected) return selected;
      const { values } = property;
      const matched = values.find(
        (value) => variable.value.toString() === value.name
      );
      if (matched) return { prop: property, value: matched };
      return null;
    },
    null
  );
  if (selectedValue)
    return {
      ...variable,
      id: selectedValue.prop.id,
      direction: selectedValue.prop.direction,
      type: selectedValue.prop.type,
      value: selectedValue.value,
      values: selectedValue.prop.values,
      order: selectedValue.prop.order,
    };
  const matched = layerProperties.find((prop) => prop.label === variable.label);
  if (matched) {
    const [firstValue] = matched.values;
    return {
      ...variable,
      id: matched.id,
      value: firstValue,
      values: matched.values,
      direction: matched.direction,
      type: matched.type,
      order: matched.order,
    };
  }
  return {
    ...variable,
    direction: "row",
    type: "checkbox",
    values: [],
    order: -1,
    value: {
      id: -1,
      name: "",
      disableSettingsVariableIds: [],
      order: -1,
    },
  };
}

/**
 *  Handles the selection via the Backend. This is not intended to use on it's own!
 *  You should use the `useSelection` hook, as it calls both URL and Persist hooks!
 */
const usePersistedSelection = (
  config: string,
  layerProperties: LayerProperties[]
) => {
  const defaultSelection = layerProperties.reduce(
    (selected: SelectionProperty[], property) => {
      const [first] = property.values;
      return [
        ...selected,
        {
          id: property.id,
          label: property.label,
          value: first,
          values: property.values,
          order: property.order,
          direction: property.direction,
          type: property.type,
        },
      ];
    },
    []
  );
  async function fetch(): Promise<Selection> {
    const response = await axios.get<SelectionResponse>(
      `/api/${config}/selection`
    );
    if (response.status === 204)
      return {
        properties: [...defaultSelection],
        indicators: new Array<IndicatorSelection>(0),
      };

    const mappedProperties = response.data.variables
      ? response.data.variables.map((variable) =>
          deserializeLayerProperty(variable, layerProperties)
        )
      : [...defaultSelection];
    //Check for missng properties, we assume the persisted call gives at least ALL default values.
    if (mappedProperties.length !== defaultSelection.length) {
      const missingProps = defaultSelection.filter(
        (defaultSel) =>
          mappedProperties.find(
            (mappedSel) => mappedSel.label === defaultSel.label
          ) === undefined
      );
      mappedProperties.push(...missingProps);
    }
    const mappedSelection = (response.data.selections || []).map(
      deserializeSelection
    );
    if (mappedSelection.filter((sel) => sel.active).length > 1) {
      const converted = mappedSelection.map((sel, i) => ({
        ...sel,
        active: i === 0,
      }));
      mappedSelection.length = 0;
      mappedSelection.push(...converted);
    }
    return {
      properties: sortBy(mappedProperties, (obj) => obj.order),
      indicators: mappedSelection,
    };
  }
  return useQuery(["selection", "persisted", config], fetch);
};

/**
 *  Handles the selection via the URL. This is not intended to use on it's own!
 *  You should use the `useSelection` hook, as it calls both URL and Persist hooks!
 */
const useURLSelection = (
  config: string,
  layerProperties: LayerProperties[]
) => {
  const [params] = useSelectionSearchParams();
  const urlSelectionIndicators = useMemo(
    () =>
      params
        .getAll("s")
        .map(urlToSelection)
        .reduce(
          (deduped: IndicatorSelection[], current: IndicatorSelection) => {
            if (
              deduped.find(
                (search: IndicatorSelection) =>
                  search.indicatorId === current.indicatorId
              ) === undefined
            )
              return [...deduped, current];
            return [...deduped];
          },
          []
        ),
    [params]
  );
  const urlSelectionProperties: SelectionProperty[] = useMemo(
    () =>
      layerProperties
        .map((prop, i) => {
          const selected = prop.values.find(
            (value) => value.id.toString() === params.get(prop.key)
          );
          if (selected)
            return {
              id: prop.id,
              label: prop.label,
              value: selected,
              direction: prop.direction,
              type: prop.type,
              values: prop.values,
              order: prop.order,
            };
          return {
            id: -1,
            label: "",
            direction: "row",
            type: "checkbox",
            order: -1,
            values: [],
            value: {
              id: -1,
              disableSettingsVariableIds: [],
              name: "",
              order: -1,
            },
          } as SelectionProperty;
        })
        .filter((selected) => selected.id > -1),
    [params, layerProperties]
  );

  function urlToSelection(
    selection: string,
    index: number
  ): IndicatorSelection {
    const [indicatorId, opacity, active, layerId] = selection.split("|");
    return {
      indicatorId: parseInt(indicatorId),
      layerId: layerId !== "null" ? parseInt(layerId) : null,
      opacity: clamp(parseFloat(opacity), 0, 1),
      active: active === "1",
      order: index,
    };
  }

  async function fetch(): Promise<Selection> {
    return Promise.resolve({
      properties: urlSelectionProperties,
      indicators: urlSelectionIndicators,
    });
  }

  return useQuery(["selection", "url", config, params.toString()], fetch);
};

export const useSelection = (
  config: string,
  layerProperties: LayerProperties[]
) => {
  const [params] = useSelectionSearchParams();

  const { data: persisted } = usePersistedSelection(config, layerProperties);
  const { data: url } = useURLSelection(config, layerProperties);

  async function queryFn(): Promise<Selection> {
    if (url && persisted) {
      const combined: Selection = {
        properties: sortBy(
          [
            ...url.properties,
            ...persisted.properties.filter(
              (perSel) =>
                url.properties.find(
                  (urlSel) => urlSel.label === perSel.label
                ) === undefined
            ),
          ],
          (obj) => obj.order
        ),
        indicators: [
          ...(url.indicators.length > 0
            ? url.indicators
            : persisted.indicators),
        ],
      };
      return Promise.resolve(combined);
    }

    return Promise.resolve<Selection>({
      properties: [],
      indicators: [],
    });
  }

  return useQuery(
    ["selection", "combined", config, params.toString()],
    queryFn,
    {
      enabled: url !== undefined && persisted !== undefined,
    }
  );
};

const useSelectionSearchParams = () => {
  const [params] = useSearchParams();
  const clonedParams = new URLSearchParams(params);
  clonedParams.delete("lat");
  clonedParams.delete("lon");
  clonedParams.delete("zoom");
  return [clonedParams];
};

/**
 *  Returns the active Indicators and selected properties
 */
export const useActiveSelection = (
  config: string,
  layerProperties: LayerProperties[]
) => {
  const [params] = useSelectionSearchParams();
  const { data: selection } = useSelection(config, layerProperties);

  function isActive(selection: IndicatorSelection) {
    return selection.active;
  }

  async function fetch(): Promise<Selection> {
    if (selection) {
      const activeSelection = selection.indicators.filter(isActive);
      const allProperties = [...selection.properties];
      const activeProperties = selection.properties.filter((currentProp) => {
        const selectedValue = allProperties.map(
          (sel) => false
          ///sel.value.disableSettingsVariableIds.includes(currentProp.id)
        );
        return selectedValue.includes(false);
      });

      return Promise.resolve({
        indicators: activeSelection,
        properties: activeProperties,
      });
    }

    return Promise.resolve<Selection>({
      properties: [],
      indicators: [],
    });
  }

  return useQuery(
    ["selection", "combined", "active", config, params.toString()],
    fetch,
    {
      enabled: selection !== undefined,
    }
  );
};

/**
 *  Handles updates to the selection via the backend. This is not intended to use on it's own!
 *  You should use the `useUpdateSelection` hook, as it calls both URL and Persist hooks!
 */
const useUpdatePersistSelection = (
  config: string,
  layerProperties: LayerProperties[]
) => {
  const queryClient = useQueryClient();

  async function update({ indicators: selection, properties }: Selection) {
    try {
      const response = await axios.put<SelectionResponse>(
        `/api/${config}/selection`,
        {
          selections: selection.map(serializeSelection),
          variables: [
            ...properties.map((property) => ({
              label: property.label,
              value: property.value.name,
            })),
          ],
        }
      );
      if (response.status === 204) return [];
      return {
        properties: (response.data.variables || [])
          .map((variable) =>
            deserializeLayerProperty(variable, layerProperties)
          )
          .filter((mapped) => mapped.id > -1),
        selection: (response.data.selections || []).map(deserializeSelection),
      };
    } catch (error) {
      if (isStatusError(error) && error.status === 401) {
        queryClient.setQueryData(["selection", "persisted", config], {
          selection,
          properties,
        });
        return {
          selection: [...selection],
          properties: [...properties],
        };
      }
      throw error;
    }
  }

  return useMutation(["selection", "persisted", config], update, {
    onMutate: (variables: Selection) => {
      const data = queryClient.getQueryData<IndicatorSelection[]>([
        "selection",
        config,
      ]);
      queryClient.setQueryData(["selection", "persisted", config], variables);

      return { data };
    },
    onSuccess: (selection) => {
      queryClient.setQueryData(["selection", "persisted", config], selection);
    },
  });
};

/**
 *  Handles updates to the selection via the URL. This is not intended to use on it's own!
 *  You should use the `useUpdateSelection` hook, as it calls both URL and Persist hooks!
 */
const useUpdateURLSelection = (
  config: string,
  layerProperties: LayerProperties[]
) => {
  const [params, setParams] = useSearchParams();

  function selectionToURL(selection: IndicatorSelection): string {
    const { indicatorId, opacity, active, layerId: variantId } = selection;
    return `${indicatorId}|${opacity}|${active ? 1 : 0}|${variantId}`;
  }

  async function update({ indicators: selection, properties }: Selection) {
    const cloned = new URLSearchParams(params);
    const [firstIndicatorSelection, ...indicatorSelection] =
      selection.map(selectionToURL);
    if (firstIndicatorSelection) {
      cloned.set("s", firstIndicatorSelection);
      indicatorSelection.forEach((sel) => cloned.append("s", sel));
    } else cloned.delete("s");
    properties.forEach((prop) =>
      cloned.set(prop.label, prop.value.id.toString())
    );
    setParams(cloned);
    return Promise.resolve(true);
  }

  return useMutation(["selection", "url", config], update);
};

export const useUpdateSelectionMutation = (
  config: string,
  layerProperties: LayerProperties[]
) => {
  const { mutateAsync: updatePersist } = useUpdatePersistSelection(
    config,
    layerProperties
  );
  const { mutateAsync: updateURL } = useUpdateURLSelection(
    config,
    layerProperties
  );

  async function update(selection: Selection) {
    await updateURL(selection);
    try {
      await updatePersist(selection);
    } catch (error) {
      return selection;
    }
    return selection;
  }

  return useMutation(["selection", "combined", config], update);
};

export const useSelectedLayer = (
  config: string,
  indicator: Indicator | undefined,
  layerProperties: LayerProperties[]
) => {
  const { data: selection } = useActiveSelection(config, layerProperties);

  async function fetch() {
    if (indicator && selection) {
      return Promise.resolve<Layer | undefined>(
        getSelectedLayer(
          indicator,
          selection.properties.map((prop) => ({
            ...prop,
            key: prop.label,
            name: prop.label,
          }))
        )
      );
    }
    return Promise.resolve(undefined);
  }

  return useQuery(
    ["selected-indicator-layer", indicator?.id, selection?.properties],
    fetch,
    {
      enabled: indicator !== undefined && selection !== undefined,
    }
  );
};

export const useSelectionHasLayers = (
  config: string,
  selection: IndicatorSelection[],
  layerProperties: LayerProperties[]
) => {
  const queryClient = useQueryClient();
  const { data: activeSelection } = useActiveSelection(config, layerProperties);
  const mapped = selection.map((sel) => {
    const [cache] = queryClient.getQueriesData<boolean>([
      "has-selected-indicator-layer",
      sel.indicatorId,
      activeSelection,
    ]);
    if (cache) {
      const [, value] = cache;
      return value;
    }
    return undefined;
  });

  return mapped;
};

export const useHasSelectedLayer = (
  config: string,
  indicator: Indicator | IndicatorBase | undefined,
  layerProperties: LayerProperties[]
) => {
  const { data: activeSelection } = useActiveSelection(config, layerProperties);
  async function fetch() {
    if (indicator && activeSelection) {
      return Promise.resolve<boolean>(
        hasSelectedLayer(
          indicator,
          activeSelection.properties.map((sel) => ({
            id: sel.id,
            key: sel.label,
            name: sel.label,
            value: sel.value,
          }))
        )
      );
    }
    return Promise.resolve(false);
  }

  return useQuery(
    ["has-selected-indicator-layer", indicator?.id, activeSelection],
    fetch,
    {
      enabled: indicator !== undefined && activeSelection !== undefined,
    }
  );
};

function hasSelectedLayer(
  indicator: Indicator | IndicatorBase,
  selectedProperties: LayerProperty[]
): boolean {
  const { layers } = indicator;
  const [firstLayer] = layers;
  return (
    (layers.find((layer) => hasLayerProperties(layer, selectedProperties)) ||
      (selectedProperties.length === 0 ? firstLayer : undefined)) !== undefined
  );
}

function getSelectedLayer(
  indicator: Indicator,
  selectedProperties: LayerProperty[]
): Layer | undefined {
  const { layers } = indicator;
  const [firstLayer] = layers;

  return (
    layers.find((layer) => hasLayerProperties(layer, selectedProperties)) ||
    (selectedProperties.length === 0 ? firstLayer : undefined)
  );
}

function hasLayerProperties(
  layer: LayerBase,
  selectedProperties: LayerProperty[]
): boolean {
  return (
    layer.properties
      .map((layerProperty) => {
        return selectedProperties.find(
          (selectedProperty) =>
            layerProperty.key === selectedProperty.key &&
            //@ts-ignore
            layerProperty.value === selectedProperty.value
        );
      })
      .includes(undefined) === false
  );
}

export function useDefaultSelection(config: string) {
  async function queryFn(): Promise<SelectionResponse> {
    const response = await axios.get<SelectionResponse>(
      `/api/${config}/defaultSelection`
    );
    return response.data;
  }
  return useQuery([config, "defaultSelection"], queryFn, { suspense: false });
}
